import { planRegistry } from "@/data/meal-plan";
import type { DayOfWeek, WeekPlan } from "@/types/meal-plan-state";
import type { OwnerContext, SaveKind, SaveWeekResult, WeekSnapshot } from "./api";
import { applyShareAction, type FriendOutcomes, type ShareAction } from "./comidas-share";
import { prepareLoadedWeek } from "./plan-switch";
import { UnsavedChangesError, WeekSaveQueue, type SaveStatus } from "./week-save-queue";

export interface FriendWriterDeps {
  fetchContext: () => Promise<OwnerContext>;
  fetchSnapshot: (weekId: string) => Promise<WeekSnapshot>;
  save: (weekId: string, plan: WeekPlan, baseVersion: number, kind: SaveKind) => Promise<SaveWeekResult>;
  today: () => string;
  debounceMs?: number;
}

export interface FriendWriterState {
  load: "loading" | "ready" | "error";
  /** Their week as I know it, my writes included (null until loaded). */
  plan: WeekPlan | null;
  saveStatus: SaveStatus;
  /** Writes queued or in flight. */
  pending: boolean;
  /**
   * Per day, what my latest action there did in their week, so "No
   * cambiamos…" stays on that row (also while typing the next days) until
   * the row is acted on again or dismissed.
   */
  outcomes: Partial<Record<DayOfWeek, FriendOutcomes>>;
  /** The day of my latest action (its row shows "guardando"). */
  lastDay: DayOfWeek | null;
}

/**
 * Writes my Comidas actions into one friend's week ("También para"). It
 * loads their week the way friend mode does (their plan and presets; a
 * missing week is built in memory and only saved with the first write),
 * applies each action with the sync rule (`applyShareAction`), and saves
 * through a versioned `WeekSaveQueue` that never saves system-only changes.
 * Actions made while their week loads wait and apply once it arrives. On a
 * 409 my unsaved actions are replayed with the sync rule on their newer
 * week (not merged blindly), so a meal they changed or ticked meanwhile
 * stays theirs. Framework free: React subscribes with `subscribe` /
 * `getState`.
 */
export class FriendWeekWriter {
  private state: FriendWriterState = {
    load: "loading",
    plan: null,
    saveStatus: "idle",
    pending: false,
    outcomes: {},
    lastDay: null,
  };
  private readonly listeners = new Set<() => void>();
  private readonly queue: WeekSaveQueue;
  private waiting: ShareAction[] = [];
  /** Actions applied since the queue was last empty: replayed on a 409. */
  private unsaved: ShareAction[] = [];
  private loading: Promise<void> | null = null;
  private context: OwnerContext | null = null;
  private loadToken = 0;
  private refetching = false;

  constructor(
    readonly friendId: number,
    readonly username: string,
    readonly weekId: string,
    private readonly deps: FriendWriterDeps,
  ) {
    this.queue = new WeekSaveQueue(
      {
        save: deps.save,
        onStatus: (saveStatus) => {
          if (!this.queue.hasPending(this.weekId)) this.unsaved = [];
          this.set({ saveStatus });
        },
        onRebase: ({ theirs }) => this.replay(theirs),
        onSaved: () => {},
      },
      deps.debounceMs,
      { saveSystemChanges: false },
    );
  }

  getState = (): FriendWriterState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private set(patch: Partial<FriendWriterState>): void {
    const pending = this.queue.hasPending(this.weekId);
    this.state = { ...this.state, ...patch, pending };
    for (const listener of this.listeners) listener();
  }

  private prepare(snapshot: WeekSnapshot, context: OwnerContext): WeekPlan {
    const { stored } = this.queue.load(this.weekId, snapshot);
    return prepareLoadedWeek(stored, this.weekId, this.deps.today(), planRegistry[context.planId].regimens, context.presets);
  }

  /** Load (or, after an error, reload) their plan, presets and week; then apply what waited. */
  load(): Promise<void> {
    const loading = this.doLoad().finally(() => {
      if (this.loading === loading) this.loading = null;
    });
    this.loading = loading;
    return loading;
  }

  private async doLoad(): Promise<void> {
    const token = ++this.loadToken;
    this.set({ load: "loading" });
    try {
      const [context, snapshot] = await Promise.all([this.deps.fetchContext(), this.deps.fetchSnapshot(this.weekId)]);
      if (token !== this.loadToken) return;
      this.context = context;
      this.set({ load: "ready", plan: this.prepare(snapshot, context) });
      const waiting = this.waiting;
      this.waiting = [];
      for (const action of waiting) this.apply(action);
    } catch (error) {
      console.error(`Failed to load ${this.username}'s week`, error);
      if (token === this.loadToken) this.set({ load: "error" });
    }
  }

  /** What `action` would do now, without doing it (null until loaded). */
  preview(action: ShareAction): ReturnType<typeof applyShareAction> | null {
    const plan = this.state.plan;
    return this.state.load === "ready" && plan ? applyShareAction(plan[action.day], action) : null;
  }

  /** Apply my action to their week and queue the save; waits (null) while their week loads. */
  apply(action: ShareAction): FriendOutcomes | null {
    const plan = this.state.plan;
    if (this.state.load !== "ready" || !plan) {
      this.waiting.push(action);
      return null;
    }
    const { day, outcomes } = applyShareAction(plan[action.day], action);
    const next = day === plan[action.day] ? plan : { ...plan, [action.day]: day };
    if (next !== plan) {
      this.unsaved.push(action);
      this.queue.enqueue(this.weekId, next, true);
    }
    this.set({ plan: next, outcomes: { ...this.state.outcomes, [action.day]: outcomes }, lastDay: action.day });
    return outcomes;
  }

  /**
   * A save hit a 409: their week changed. Redo my unsaved actions on their
   * copy with the sync rule, so a meal they edited or ticked meanwhile is
   * kept (and reported) instead of overwritten; the queue retries with it.
   */
  private replay(theirs: WeekPlan): void {
    const context = this.context;
    let plan = context
      ? prepareLoadedWeek(theirs, this.weekId, this.deps.today(), planRegistry[context.planId].regimens, context.presets)
      : theirs;
    const prepared = plan;
    const outcomes = { ...this.state.outcomes };
    for (const action of this.unsaved) {
      const result = applyShareAction(plan[action.day], action);
      if (result.day !== plan[action.day]) plan = { ...plan, [action.day]: result.day };
      outcomes[action.day] = result.outcomes;
    }
    // Nothing of mine is left to write: queue their copy as is (nothing to
    // save), not the prepared one, whose preset fills nobody asked for.
    this.queue.enqueue(this.weekId, plan === prepared ? theirs : plan, true);
    this.set({ plan, outcomes });
  }

  /** "Dejar": forget a day's outcomes (their kept meal stays theirs, without the warning). */
  dismiss(day: DayOfWeek): void {
    if (!this.state.outcomes[day]) return;
    const { [day]: _, ...rest } = this.state.outcomes;
    this.set({ outcomes: rest });
  }

  /** Back on the tab: pick up their changes (or, with writes pending, save them: a 409 merges). */
  async refetch(): Promise<void> {
    if (this.state.load === "error") return this.load();
    if (this.state.load !== "ready" || !this.context || this.refetching) return;
    if (this.queue.hasPending(this.weekId)) return this.retry();
    this.refetching = true;
    const token = this.loadToken;
    try {
      const snapshot = await this.deps.fetchSnapshot(this.weekId);
      if (token !== this.loadToken || this.queue.hasPending(this.weekId)) return;
      if (snapshot.version === this.queue.baseVersion(this.weekId)) return;
      this.set({ plan: this.prepare(snapshot, this.context) });
    } catch (error) {
      console.error(`Failed to refresh ${this.username}'s week`, error);
    } finally {
      this.refetching = false;
    }
  }

  /** Retry a failed load or save. */
  retry(): Promise<void> {
    if (this.state.load === "error") return this.load();
    return this.queue.drain().catch((error: unknown) => console.error(`Failed to save ${this.username}'s week`, error));
  }

  /**
   * Save now, including actions still waiting for their week to load;
   * rejects while anything stays unsaved (logout, leaving).
   */
  async flush(): Promise<void> {
    if (this.loading) await this.loading;
    if (this.waiting.length > 0) throw new UnsavedChangesError();
    await this.queue.flush();
  }

  /** Stop notifying. A load in flight still finishes, so a flush started before keeps its writes. */
  dispose(): void {
    this.listeners.clear();
  }
}
